import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AliquotaIbsService } from '@shared/services/apis/motor-fiscal/aliquota-ibs.service';
import { MunicipioService } from '@shared/services/apis/usuario/municipio.service';
import { UnidadeFederativaService, UfOption } from '@shared/services/apis/usuario/unidade-federativa.service';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AliquotaIbsEditDialogComponent, AliquotaIbsEditDialogData, AliquotaIbsEditDialogResult } from '../aliquota-ibs-edit-dialog/aliquota-ibs-edit-dialog.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { LinhaMunicipio } from './linha-municipio.struct';

export { LinhaMunicipio } from './linha-municipio.struct';

/** Listagem/edição das alíquotas IBS por município (filtradas por UF), paginada no backend. Salvamento direto. */
@Component({
    selector: 'app-aliquota-ibs-municipio-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTableModule,
        MatProgressSpinnerModule,
        EmptyStateComponent,
        LoadingBoxDefaultListComponent,
    ],
    templateUrl: './aliquota-ibs-municipio-dialog.component.html',
    styleUrl: './aliquota-ibs-municipio-dialog.component.css',
})
export class AliquotaIbsMunicipioDialogComponent implements OnInit {
    public isLoading = false;
    public pesquisa = '';
    public idUfSelecionada: number | null = null;
    public ufs: UfOption[] = [];
    public displayedColumns = ['municipio', 'aliquota', 'vigencia', 'actions'];

    public linhas: LinhaMunicipio[] = [];

    private readonly _tamanhoPagina = 100;
    private _offset = 0;
    public _possuiProximaPagina = false;

    private readonly pesquisaChanged = new Subject<void>();

    constructor(
        private dialogRef: MatDialogRef<AliquotaIbsMunicipioDialogComponent>,
        private dialog: MatDialog,
        private aliquotaService: AliquotaIbsService,
        private municipioService: MunicipioService,
        private ufService: UnidadeFederativaService,
        private alertService: AlertService,
    ) {}

    ngOnInit(): void {
        this.ufService.getUnidadesFederativas().subscribe({
            next: ufs => { this.ufs = ufs; },
            error: () => this.alertService.show('Erro', 'Não foi possível carregar as UFs.', AlertType.error),
        });

        // Pesquisa agora consulta o backend a cada mudança; debounce evita 1 request por tecla.
        this.pesquisaChanged.pipe(debounceTime(300)).subscribe(() => this.recarregarDoInicio());
    }

    public onUfChange(): void {
        this.pesquisa = '';
        this.recarregarDoInicio();
    }

    public filtrar(): void {
        this.pesquisaChanged.next();
    }

    public proximaPagina(): void {
        this._offset += this._tamanhoPagina;
        this.carregarPagina();
    }

    private recarregarDoInicio(): void {
        this._offset = 0;
        this._possuiProximaPagina = false;
        this.linhas = [];
        if (this.idUfSelecionada == null) return;
        this.carregarPagina();
    }

    private carregarPagina(): void {
        if (this.idUfSelecionada == null) return;

        this.isLoading = true;
        const textoBusca = this.pesquisa.trim() || null;

        this.municipioService.getByUf(this.idUfSelecionada, textoBusca, this._offset, this._tamanhoPagina).subscribe({
            next: res => {
                const municipios = res?.list ?? [];
                // O endpoint não informa se há próxima página: uma página cheia indica que
                // provavelmente há mais. No múltiplo exato do tamanho, o botão fica habilitado
                // por um clique a mais, que volta vazio e então o desabilita.
                this._possuiProximaPagina = municipios.length === this._tamanhoPagina;

                if (municipios.length === 0) {
                    this.isLoading = false;
                    return;
                }

                const ids = municipios.map(m => m.idMunicipio);
                this.aliquotaService.getIbsMunicipioEfetivas(ids).subscribe({
                    next: aliq => {
                        const porId = new Map((aliq?.itens ?? []).map(a => [a.id, a]));
                        const novasLinhas: LinhaMunicipio[] = municipios.map(m => {
                            const a = porId.get(m.idMunicipio);
                            return {
                                idMunicipio: m.idMunicipio,
                                nome: m.nome,
                                uf: m.uf ?? '',
                                pAliquota: a?.pAliquota ?? 0,
                                dataVigenciaInicio: a?.dataVigenciaInicio ?? null,
                                dataVigenciaFim: a?.dataVigenciaFim ?? null,
                                personalizada: a?.personalizada ?? false,
                            };
                        });
                        this.linhas = [...this.linhas, ...novasLinhas];
                        this.isLoading = false;
                    },
                    error: () => { this.isLoading = false; this.alertService.show('Erro', 'Não foi possível carregar as alíquotas.', AlertType.error); },
                });
            },
            error: () => { this.isLoading = false; this.alertService.show('Erro', 'Não foi possível carregar os municípios.', AlertType.error); },
        });
    }

    public editar(linha: LinhaMunicipio): void {
        const data: AliquotaIbsEditDialogData = {
            titulo: 'Alíquota IBS Município',
            descricao: `${linha.nome}${linha.uf ? ' - ' + linha.uf : ''}`,
            pAliquota: linha.pAliquota,
        };
        const ref = this.dialog.open(AliquotaIbsEditDialogComponent, { width: '420px', maxWidth: '95vw', data });
        ref.afterClosed().subscribe((result: AliquotaIbsEditDialogResult | undefined) => {
            if (!result) return;
            this.aliquotaService.salvarIbsMunicipio(linha.idMunicipio, result.pAliquota).subscribe({
                next: r => {
                    if (r?.isError) {
                        this.alertService.show('Erro', r.errorDescription || 'Não foi possível salvar a alíquota.', AlertType.error);
                        return;
                    }
                    linha.pAliquota = result.pAliquota;
                    linha.personalizada = true;
                    this.alertService.show('Sucesso', 'Alíquota salva com sucesso.', AlertType.success);
                },
                error: () => this.alertService.show('Erro', 'Não foi possível salvar a alíquota.', AlertType.error),
            });
        });
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
